import {useContext, useState} from "react";
import {useNavigate} from "react-router-dom";
import {AuthContext} from "../context/AuthContext";
import AuthLayout from "../components/AuthLayout";
import {Alert, Input, Select, buttonClass} from "../components/ui";
import axiosInstance from "../utils/axiosInstance";
import {countryOptions} from "../utils/Countries";

const Register = () => {
    const [error, setError] = useState(null);
    const {register} = useContext(AuthContext);
    const [username, setUsername] = useState("");
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordStrength, setPasswordStrength] = useState("");
    const [phone, setPhone] = useState("");
    const [phoneCode, setPhoneCode] = useState("");
    const [country, setCountry] = useState("");
    const [submitLoading, setSubmitLoading] = useState(false);
    const navigate = useNavigate();

    const handleCountryChange = (e) => {
        const selectedCountry = e.target.value;
        const countryData = countryOptions.find(
            (option) => option.name === selectedCountry
        );
        setCountry(selectedCountry);
        setPhoneCode(countryData ? countryData.code : "");
    };

    const evaluatePasswordStrength = (password) => {
        let strength = "weak";
        if (password.length >= 8) {
            if (
                /[A-Z]/.test(password) &&
                /[a-z]/.test(password) &&
                /\d/.test(password) &&
                /[!@#$%^&*(),.?":{}|<>]/.test(password)
            ) {
                strength = "strong";
            } else if (
                /[A-Z]/.test(password) ||
                /\d/.test(password) ||
                /[!@#$%^&*(),.?":{}|<>]/.test(password)
            ) {
                strength = "medium";
            }
        }
        return strength;
    };

    const handlePasswordChange = (e) => {
        const pwd = e.target.value;
        setPassword(pwd);
        const strength = evaluatePasswordStrength(pwd);
        setPasswordStrength(strength);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        const newUser = {
            username,
            full_name: fullName,
            email,
            password,
            phone: `${phoneCode}${phone}`,
            country,
            credit_balance: 0,
            role: "CUSTOMER",
            status: "ACTIVE",
        };

        try {
            setSubmitLoading(true);
            const response = await axiosInstance.post("/register", newUser);

            if (response.status === 201 || response.status === 200) {
                register(newUser, navigate);
            } else {
                setError("Registration failed");
            }
            setSubmitLoading(false);
        } catch (error) {
            setSubmitLoading(false);
            if (error.response?.status === 400) {
                setError(error.response.data.message);
                return;
            }
            setError("An error occurred during registration");
        }
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength === "strong") return "text-emerald-600 dark:text-emerald-400";
        if (passwordStrength === "medium") return "text-amber-600 dark:text-amber-400";
        return "text-rose-600 dark:text-rose-400";
    };

    return (
        <AuthLayout
            title="Create Account"
            subtitle="Fill in the information to create a new account."
        >
            {error && <Alert tone="error" className="mb-4">{error}</Alert>}

            <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                />
                <Input
                    type="text"
                    placeholder="Full Name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                />
                <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                />

                <Select value={country} onChange={handleCountryChange} required>
                    <option value="" disabled>
                        Select Country
                    </option>
                    {countryOptions.map((option) => (
                        <option key={option.name} value={option.name}>
                            {option.name}
                        </option>
                    ))}
                </Select>

                <div className="flex gap-2">
                    <Input
                        type="text"
                        value={phoneCode}
                        placeholder="+49"
                        onChange={(e) => setPhoneCode(e.target.value)}
                        className="w-1/4"
                        required
                        readOnly
                    />
                    <Input
                        type="tel"
                        placeholder="Phone Number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-3/4"
                        required
                    />
                </div>

                <div>
                    <Input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={handlePasswordChange}
                        required
                    />
                    {passwordStrength ? (
                        <p className={`mt-1.5 text-xs font-bold uppercase tracking-[0.08em] ${getPasswordStrengthColor()}`}>
                            Password strength: {passwordStrength}
                        </p>
                    ) : (
                        <p className="mt-1.5 text-xs text-gray-400 dark:text-slate-500">
                            At least 8 characters, mixing case, a number and a symbol.
                        </p>
                    )}
                </div>

                <Input
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                />

                <button
                    type="submit"
                    className={buttonClass({className: "w-full"})}
                    disabled={
                        submitLoading ||
                        passwordStrength === "" ||
                        passwordStrength === "weak"
                    }
                >
                    {submitLoading ? "Sending…" : "CREATE NEW ACCOUNT"}
                </button>
            </form>

            <p className="mt-5 text-center text-sm text-gray-500 dark:text-slate-400">
                Already have an account?{" "}
                <a
                    href="/login"
                    className="font-semibold text-red-600 transition hover:text-red-500 dark:text-orange-400"
                >
                    Log in
                </a>
            </p>
        </AuthLayout>
    );
};

export default Register;
